import { Box, Button, Stack, Typography } from "@mui/material";
import { useTransactionPay } from "Api/Mutations/Transactions/useTransactionPay";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import { getPayButtonLabel } from "Components/Customer/Payment/PaymentForms/payButtonUtils";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { BankingAccountSelector } from "Components/Shared/Inputs/Form/BankingAccountSelector";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import {
  getResourcePath,
  Resources,
  useResource,
} from "Translations/Resources";
import {
  type ErrorType,
  handleTransactionPayError,
} from "Utils/TransactionErrorsUtils";
import { useForm } from "react-hook-form";
import { BankAccountsSkeleton } from "Components/AnonymousCustomer/Payment/AccountSelection/BankAccountsSkeleton";
import { usePISPAuthenticate } from "Api/Mutations/Transactions/usePISPAuthenticate";
import { BankAccountStatus } from "Infrastructure/Api/Api";
import { VerificationStatusChip } from "Components/Shared/Account/VerificationStatusChip";
import { Trans } from "react-i18next";

const RESOURCES = Resources.Customer.Pay;
const ANONYMOUS_RESOURCES =
  Resources.Pages.Content.CustomerAnonymousPaymentPay.AccountSelection;

type FormModel = {
  bankAccountID: number;
};

type Props = {
  transactionPublicID: string;
  onAddBankAccount?: () => void;
  onError?: () => void;
};

export const BankAccountSelector = ({
  transactionPublicID,
  onAddBankAccount,
  onError,
}: Props) => {
  const { t } = useResource();
  const { data, isLoading } = useBankingAccounts();
  const { data: transaction } = useTransactionsDetail({
    publicID: transactionPublicID,
  });
  const { mutate: fetchAndRedirectToPISPURL } = usePISPAuthenticate();
  const { mutate: payTransaction, isPending } = useTransactionPay();

  const defaultBankAccountID = data?.items[0]?.bankAccountID;
  const { control, handleSubmit, watch } = useForm<FormModel>({
    values:
      defaultBankAccountID != null
        ? { bankAccountID: defaultBankAccountID }
        : undefined,
  });
  const selectedBankAccountID = watch("bankAccountID");

  const onSubmit = async ({ bankAccountID }: FormModel) => {
    const selectedAccount = data?.items.find(
      (account) => account.bankAccountID === bankAccountID,
    );
    payTransaction(
      {
        publicID: transactionPublicID,
        senderBankAccountID: bankAccountID,
      },
      {
        onError: (error) => {
          const e = error as unknown as ErrorType;
          if (e.data?.errors.pispAuthentication) {
            fetchAndRedirectToPISPURL({
              environment: selectedAccount?.environment,
              transactionPublicID: transactionPublicID,
            });
          }
          handleTransactionPayError(e, t);
          onError?.();
        },
      },
    );
  };

  return (
    <Stack gap={4} height="100%">
      <Typography>{t(RESOURCES.ChooseBankAccount)}</Typography>
      <InfoBox variant="info" content={t(ANONYMOUS_RESOURCES.InfoBox)} />
      <Stack
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        justifyContent={["space-between", "start"]}
        height={["100%"]}
        gap={3}
      >
        {isLoading ? (
          <BankAccountsSkeleton />
        ) : (
          <BankingAccountSelector
            bankAccounts={data?.items ?? []}
            control={control}
            name="bankAccountID"
            additionalContent={
              <Stack mt={2}>
                <Typography lineHeight={1.7} px={2}>
                  <Box mb={0.5} mr={1} component="span">
                    <VerificationStatusChip
                      verificationStatus={BankAccountStatus.Restricted}
                    />
                  </Box>
                  <Trans
                    i18nKey={getResourcePath(
                      RESOURCES.MooneLiteAccountExplanation,
                    )}
                    components={{ newline: <br /> }}
                  />
                </Typography>
                <Button
                  type="button"
                  color="info"
                  variant="text"
                  onClick={onAddBankAccount}
                  sx={{ textTransform: "none", justifyContent: "flex-end" }}
                >
                  {t(RESOURCES.AddNextBankAccount)}
                </Button>
              </Stack>
            }
          />
        )}
        <PrimaryButton
          fullWidth
          type="submit"
          color="primary"
          disabled={selectedBankAccountID == null}
          isLoading={isPending}
        >
          {getPayButtonLabel(t, transaction?.amount, transaction?.currencyCode)}
        </PrimaryButton>
      </Stack>
    </Stack>
  );
};
